import React, { useState, useEffect, useCallback, useRef } from "react";
import SimulationScheduleAPI from "../services/SimulationScheduleAPI";
import SimulationHeuristicsAPI from "../services/SimulationHeuristicsAPI";
import RefreshButton from "../common/RefreshButton";
import { useUser } from "../context/UserContext";
import { showToastWithAction } from "../utils/ToastWithAction";
import { formatDateTime as formatDateTimeUtil } from "../utils/DateTimeUtil";
import DateRangeField from "../../common/datetime/DateRangeField";
import "./css/ExecuteSimulationSchedule.css";
import LoadingSpinner from "../common/LoadingSpinner";

function ExecuteSimulationSchedule() {
  const { selectedUser } = useUser();
  const [schedules, setSchedules] = useState([]);
  const [allSchedules, setAllSchedules] = useState([]); // Store all schedules for all users
  const [filteredSchedules, setFilteredSchedules] = useState([]); // Store filtered schedules
  const [groupedSchedules, setGroupedSchedules] = useState({}); // Schedules grouped by user
  const [messageDetails, setMessageDetails] = useState({}); // Store message details by messageUuid
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [executingSchedules, setExecutingSchedules] = useState(new Set());
  const [executingAll, setExecutingAll] = useState(false);
  const [deletingSchedules, setDeletingSchedules] = useState(new Set());

  // View state
  const [currentView, setCurrentView] = useState("calendar"); // "list" or "calendar" - calendar is default
  const [selectedSchedule, setSelectedSchedule] = useState(null); // For modal
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  // Calendar navigation state
  const [calendarDate, setCalendarDate] = useState(new Date()); // Current calendar month/year
  const today = new Date(); // Today's date for calendar highlighting
  const calendarScrollRef = useRef(null); // Reference for smooth scrolling

  // Search state
  const [searchCriteria, setSearchCriteria] = useState({
    dateFrom: "",
    dateTo: "",
    userEmail: "",
    status: "",
    channel: "",
  });
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [uniqueUserEmails, setUniqueUserEmails] = useState([]);
  const [uniqueChannels, setUniqueChannels] = useState([]);

  const fetchMessageDetails = async (messageUuids) => {
    const uniqueMessageUuids = [
      ...new Set(messageUuids.filter((uuid) => uuid && !messageDetails[uuid])),
    ];

    if (uniqueMessageUuids.length === 0) {
      return;
    }

    console.log("Fetching message details for UUIDs:", uniqueMessageUuids);

    const messagePromises = uniqueMessageUuids.map(async (messageUuid) => {
      try {
        const messageData =
          await SimulationHeuristicsAPI.getMessageById(messageUuid);
        return { messageUuid, data: messageData };
      } catch (error) {
        console.error(`Error fetching message ${messageUuid}:`, error);
        return { messageUuid, data: null };
      }
    });

    try {
      const results = await Promise.all(messagePromises);
      const newMessageDetails = {};

      results.forEach(({ messageUuid, data }) => {
        if (data) {
          newMessageDetails[messageUuid] = data;
        }
      });

      setMessageDetails((prev) => ({ ...prev, ...newMessageDetails }));
    } catch (error) {
      console.error("Error fetching message details:", error);
    }
  };

  // Extract unique user emails and channels from schedules
  const extractUniqueValues = (schedules) => {
    const emails = [
      ...new Set(
        schedules
          .map((schedule) => schedule.userEmail)
          .filter((email) => email),
      ),
    ];
    const channels = [
      ...new Set(
        schedules
          .map((schedule) => schedule.communicationChannel)
          .filter((channel) => channel),
      ),
    ];

    setUniqueUserEmails(emails.sort());
    setUniqueChannels(channels.sort());
  };

  // Apply search filters
  const applySearchFilters = (schedules) => {
    let filtered = [...schedules];

    // Date range filter
    if (searchCriteria.dateFrom) {
      const fromDate = new Date(searchCriteria.dateFrom);
      filtered = filtered.filter(
        (schedule) => new Date(schedule.scheduledFor) >= fromDate,
      );
    }

    if (searchCriteria.dateTo) {
      const toDate = new Date(searchCriteria.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(
        (schedule) => new Date(schedule.scheduledFor) <= toDate,
      );
    }

    // User email filter
    if (searchCriteria.userEmail) {
      filtered = filtered.filter(
        (schedule) =>
          schedule.userEmail &&
          schedule.userEmail
            .toLowerCase()
            .includes(searchCriteria.userEmail.toLowerCase()),
      );
    }

    // Status filter
    if (searchCriteria.status) {
      filtered = filtered.filter(
        (schedule) => schedule.status === searchCriteria.status,
      );
    }

    // Channel filter
    if (searchCriteria.channel) {
      filtered = filtered.filter(
        (schedule) => schedule.communicationChannel === searchCriteria.channel,
      );
    }

    return filtered;
  };

  // Handle search criteria changes
  const handleSearchChange = (field, value) => {
    setSearchCriteria((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Clear search filters
  const clearSearch = () => {
    setSearchCriteria({
      dateFrom: "",
      dateTo: "",
      userEmail: "",
      status: "",
      channel: "",
    });
  };

  // Check if any search criteria is active
  const hasActiveSearch = () => {
    return Object.values(searchCriteria).some((value) => value !== "");
  };

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      // Prepare API parameters
      const apiParams = {};

      // If a specific user is selected, fetch only their schedules
      if (selectedUser && selectedUser.uuid) {
        apiParams.userUuid = selectedUser.uuid;
        console.log(
          `Fetching schedules for selected user: ${selectedUser.uuid}`,
        );
      } else {
        console.log("Fetching all schedules (no user selected)");
      }

      // Fetch schedules from the backend with optional user filtering
      const response = await SimulationScheduleAPI.getSchedules(apiParams);

      console.log("🔍 API Response received:", response);
      console.log("🔍 Response type:", typeof response);
      console.log("🔍 Response success:", response?.success);
      console.log("🔍 Response schedules:", response?.schedules);
      console.log("🔍 Schedules length:", response?.schedules?.length);

      if (response && response.success && response.schedules) {
        const fetchedSchedules = response.schedules;
        console.log("✅ Fetched schedules:", fetchedSchedules);

        // Extract message UUIDs for fetching message details
        const messageUuids = fetchedSchedules
          .map((schedule) => schedule.messageUuid)
          .filter((uuid) => uuid);

        if (selectedUser && selectedUser.uuid) {
          // When filtering by user, the response should already contain only that user's schedules
          setSchedules(fetchedSchedules);
          setAllSchedules(fetchedSchedules); // For consistency
          setGroupedSchedules({
            [selectedUser.userId || selectedUser.user_id]: fetchedSchedules,
          });
        } else {
          // When fetching all schedules, group them by user
          setAllSchedules(fetchedSchedules);

          // Group schedules by user
          const grouped = fetchedSchedules.reduce((acc, schedule) => {
            const userId = schedule.userId;
            if (!acc[userId]) {
              acc[userId] = [];
            }
            acc[userId].push(schedule);
            return acc;
          }, {});
          setGroupedSchedules(grouped);
          setSchedules(fetchedSchedules);
        }

        // Extract unique values for search dropdowns
        extractUniqueValues(fetchedSchedules);

        // Apply search filters
        const filtered = applySearchFilters(fetchedSchedules);
        console.log("🔍 Filtered schedules:", filtered);
        console.log("🔍 Filtered schedules length:", filtered.length);
        setFilteredSchedules(filtered);

        // Fetch message details for all unique message IDs
        await fetchMessageDetails(messageUuids);
      } else {
        console.warn("❌ No schedules data received from API");
        console.warn("❌ Response structure:", {
          hasResponse: !!response,
          hasSuccess: response?.success,
          hasSchedules: response?.schedules,
          responseKeys: response ? Object.keys(response) : [],
        });
        setSchedules([]);
        setAllSchedules([]);
        setGroupedSchedules({});
      }
    } catch (error) {
      console.error("Error fetching schedules:", error);
      setMessage(`Error fetching schedules: ${error.message}`);
      setSchedules([]);
      setAllSchedules([]);
      setGroupedSchedules({});
    } finally {
      setLoading(false);
    }
  }, [selectedUser]);

  // Fetch schedules from backend
  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  // Update filtered schedules when search criteria changes
  useEffect(() => {
    console.log("🔍 useEffect triggered - updating filtered schedules");
    console.log("🔍 Current schedules length:", schedules.length);
    console.log("🔍 Current allSchedules length:", allSchedules.length);
    console.log("🔍 Selected user:", selectedUser);
    console.log("🔍 Search criteria:", searchCriteria);

    const schedulesToFilter = selectedUser ? schedules : allSchedules;
    console.log("🔍 Schedules to filter length:", schedulesToFilter.length);

    const filtered = applySearchFilters(schedulesToFilter);
    console.log("🔍 Filtered result length:", filtered.length);
    setFilteredSchedules(filtered);
  }, [searchCriteria, schedules, allSchedules, selectedUser]);

  const handleExecuteSchedule = async (scheduleId) => {
    setExecutingSchedules((prev) => new Set(prev).add(scheduleId));
    setMessage("");

    try {
      const executeData = {
        scheduleId: scheduleId,
        userId: selectedUser?.userId || selectedUser?.user_id,
        executedBy: "manual", // Indicate this is a manual execution
        executedAt: new Date().toISOString(),
      };

      console.log(
        "Executing single simulation schedule with data:",
        executeData,
      );

      // Call the API to execute the specific schedule
      const response = await SimulationScheduleAPI.executeSchedule(executeData);

      console.log("Schedule executed successfully:", response);

      if (response && response.success) {
        setMessage(`Simulation schedule #${scheduleId} executed successfully!`);

        // Update the schedule status locally
        setSchedules((prev) =>
          prev.map((schedule) =>
            schedule.scheduleId === scheduleId || schedule.id === scheduleId
              ? { ...schedule, status: "EXECUTING" }
              : schedule,
          ),
        );

        // Refresh schedules after a short delay
        setTimeout(() => {
          fetchSchedules();
        }, 2000);
      } else {
        throw new Error(response?.message || "Failed to execute schedule");
      }
    } catch (error) {
      console.error("Error executing simulation schedule:", error);
      setMessage(
        `Error: ${error.message || "Failed to execute simulation schedule"}`,
      );
    } finally {
      setExecutingSchedules((prev) => {
        const newSet = new Set(prev);
        newSet.delete(scheduleId);
        return newSet;
      });
    }
  };

  const handleExecuteAllSchedules = async () => {
    setExecutingAll(true);
    setMessage("");

    try {
      console.log("Executing all simulation schedules");

      // Call the API to execute all pending schedules
      const response = await SimulationScheduleAPI.executeAllSchedules();

      console.log("All schedules execution response:", response);

      if (response && response.success) {
        const { schedulesExecuted, emailsSent, failedScheduleErrors } =
          response;

        let successMessage = `Successfully executed ${schedulesExecuted} schedules`;
        if (emailsSent) {
          successMessage += ` and sent ${emailsSent} emails`;
        }

        if (failedScheduleErrors && failedScheduleErrors.length > 0) {
          successMessage += `. ${failedScheduleErrors.length} schedules failed.`;
        }

        setMessage(successMessage);

        // Refresh schedules to get updated status
        setTimeout(() => {
          fetchSchedules();
        }, 2000);
      } else {
        throw new Error(response?.message || "Failed to execute schedules");
      }
    } catch (error) {
      console.error("Error executing all simulation schedules:", error);
      setMessage(
        `Error: ${
          error.message || "Failed to execute all simulation schedules"
        }`,
      );
    } finally {
      setExecutingAll(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    setDeletingSchedules((prev) => new Set(prev).add(scheduleId));
    setMessage("");

    try {
      console.log("Deleting simulation schedule:", scheduleId);

      // Show confirmation dialog
      showToastWithAction(
        "Delete this simulation schedule? This action cannot be undone.",
        async () => {
          try {
            // Call the API to delete the schedule
            const response =
              await SimulationScheduleAPI.deleteSchedule(scheduleId);

            console.log("Delete schedule response:", response);

            if (response && response.success) {
              setMessage(`Schedule #${scheduleId} deleted successfully!`);

              // Remove the schedule from local state immediately
              setSchedules((prev) =>
                prev.filter(
                  (schedule) =>
                    (schedule.scheduleId || schedule.id) !== scheduleId,
                ),
              );

              setAllSchedules((prev) =>
                prev.filter(
                  (schedule) =>
                    (schedule.scheduleId || schedule.id) !== scheduleId,
                ),
              );

              // Update grouped schedules
              setGroupedSchedules((prev) => {
                const newGrouped = { ...prev };
                Object.keys(newGrouped).forEach((userId) => {
                  newGrouped[userId] = newGrouped[userId].filter(
                    (schedule) =>
                      (schedule.scheduleId || schedule.id) !== scheduleId,
                  );
                  // Remove empty groups
                  if (newGrouped[userId].length === 0) {
                    delete newGrouped[userId];
                  }
                });
                return newGrouped;
              });

              // Refresh schedules after a short delay to ensure consistency
              setTimeout(() => {
                fetchSchedules();
              }, 1000);
            } else {
              throw new Error(response?.message || "Failed to delete schedule");
            }
          } catch (error) {
            console.error("Error deleting simulation schedule:", error);
            setMessage(
              `Error deleting schedule: ${
                error.message || "Failed to delete schedule"
              }`,
            );
          }
        },
        5,
      );
    } finally {
      setDeletingSchedules((prev) => {
        const newSet = new Set(prev);
        newSet.delete(scheduleId);
        return newSet;
      });
    }
  };

  // Use centralized DateTimeUtil for consistent date/time formatting
  const formatDateTime = (dateString) => {
    return formatDateTimeUtil(dateString);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "#ffc107";
      case "EXECUTING":
        return "#007bff";
      case "COMPLETED":
        return "#28a745";
      case "FAILED":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  const getUserDisplayName = (userUuid) => {
    // If we have user data in the schedule, use it
    // Otherwise, try to find the user in selected user or return a default
    return `User ${userUuid}`;
  };

  const renderSchedulesByUser = () => {
    const schedulesToRender = filteredSchedules;
    console.log("🎨 Rendering schedules, count:", schedulesToRender.length);
    console.log("🎨 First schedule sample:", schedulesToRender[0]);

    if (selectedUser) {
      // Show only selected user's schedules
      const userSchedules = schedulesToRender.filter(
        (schedule) => schedule.userUuid === selectedUser.uuid,
      );

      return (
        <div className="user-section">
          <h3>
            {selectedUser.firstName} {selectedUser.lastName} (
            {selectedUser.email})
          </h3>
          <div className="schedules-list">
            {userSchedules.map((schedule) => renderScheduleCard(schedule))}
          </div>
        </div>
      );
    } else {
      // Show all users' schedules grouped by user
      const groupedFiltered = schedulesToRender.reduce((acc, schedule) => {
        const userUuid = schedule.userUuid;
        if (!acc[userUuid]) {
          acc[userUuid] = [];
        }
        acc[userUuid].push(schedule);
        return acc;
      }, {});

      return Object.entries(groupedFiltered).map(
        ([userUuid, userSchedules]) => (
          <div key={userUuid} className="user-section">
            <h3>{getUserDisplayName(userUuid)}</h3>
            <div className="schedules-list">
              {userSchedules.map((schedule) => renderScheduleCard(schedule))}
            </div>
          </div>
        ),
      );
    }
  };

  const renderScheduleCard = (schedule) => {
    const scheduleId = schedule.scheduleId || schedule.id;
    const messageUuid = schedule.messageUuid;
    const messageData = messageDetails[messageUuid];

    // Extract first line of content for preview
    const getContentPreview = (content) => {
      if (!content) return "No content available";
      const firstLine = content.split("\n")[0];
      return firstLine.length > 100
        ? firstLine.substring(0, 100) + "..."
        : firstLine;
    };

    // Get message subject and content from fetched data or fallback
    const messageSubject =
      messageData?.subject ||
      schedule.messageSubject ||
      schedule.subject ||
      "No subject";
    const messageContent =
      messageData?.content ||
      schedule.messageContent ||
      schedule.content ||
      "No content available";

    return (
      <div key={scheduleId} className="schedule-card-horizontal">
        <div className="schedule-header-horizontal">
          <div className="schedule-id-status">
            <h3>Schedule #{scheduleId}</h3>
            <span
              className="status-badge"
              style={{ backgroundColor: getStatusColor(schedule.status) }}
            >
              {schedule.status}
            </span>
          </div>

          <div className="schedule-time">
            <span className="label">Scheduled For:</span>
            <span
              className={`value ${
                isPastDue(schedule.scheduledFor) &&
                schedule.status === "PENDING"
                  ? "past-due"
                  : ""
              }`}
            >
              {formatDateTime(schedule.scheduledFor)}
              {isPastDue(schedule.scheduledFor) &&
                schedule.status === "PENDING" && (
                  <span className="past-due-indicator"> (Past Due)</span>
                )}
            </span>
          </div>

          {/* Show completed date for completed schedules */}
          {schedule.status === "COMPLETED" && schedule.executedAt && (
            <div className="schedule-completed-time">
              <span className="label">Completed At:</span>
              <span className="value completed-time">
                {formatDateTime(schedule.executedAt)}
              </span>
            </div>
          )}
        </div>

        <div className="schedule-message-info">
          <div className="message-subject">
            <span className="label">Subject:</span>
            <span className="value">
              {messageUuid && !messageData ? "Loading..." : messageSubject}
            </span>
          </div>

          <div className="message-content-preview">
            <span className="label">Content:</span>
            <span className="value content-preview">
              {messageUuid && !messageData
                ? "Loading message content..."
                : getContentPreview(messageContent)}
            </span>
          </div>
        </div>

        <div className="schedule-details-horizontal">
          <div className="detail-item">
            <span className="label">Message ID:</span>
            <span className="value">
              {schedule.messageId || schedule.templateId || "N/A"}
            </span>
          </div>

          <div className="detail-item">
            <span className="label">Profile ID:</span>
            <span className="value">
              {schedule.simulationProfileId || "N/A"}
            </span>
          </div>

          <div className="detail-item">
            <span className="label">Frequency:</span>
            <span className="value">{schedule.frequency || "N/A"}</span>
          </div>

          <div className="detail-item">
            <span className="label">Sophistication:</span>
            <span className="value">
              {schedule.sophisticationLevel || "N/A"}/10
            </span>
          </div>

          <div className="detail-item">
            <span className="label">Context:</span>
            <span className="value">
              {schedule.communicationContext || "N/A"}
            </span>
          </div>

          <div className="detail-item">
            <span className="label">Channel:</span>
            <span className="value">
              {schedule.communicationChannel || "Not specified"}
            </span>
          </div>
        </div>

        <div className="schedule-actions-horizontal">
          {schedule.status === "PENDING" && (
            <button
              className="btn-execute"
              onClick={() => handleExecuteSchedule(scheduleId)}
              disabled={executingSchedules.has(scheduleId)}
            >
              {executingSchedules.has(scheduleId)
                ? "Executing..."
                : "Execute Now"}
            </button>
          )}

          {schedule.status === "EXECUTING" && (
            <div className="executing-indicator">
              <span>Executing...</span>
            </div>
          )}

          {schedule.status === "COMPLETED" && (
            <div className="completed-indicator">
              <span>✓ Completed</span>
            </div>
          )}

          {/* Delete button - available for all statuses except EXECUTING */}
          {schedule.status !== "EXECUTING" && (
            <button
              className="btn-delete"
              onClick={() => handleDeleteSchedule(scheduleId)}
              disabled={deletingSchedules.has(scheduleId)}
              title="Delete this schedule"
            >
              {deletingSchedules.has(scheduleId) ? "Deleting..." : "🗑️ Delete"}
            </button>
          )}
        </div>
      </div>
    );
  };

  const isPastDue = (scheduledFor) => {
    return new Date(scheduledFor) < new Date();
  };

  // Calendar view functions
  const handleScheduleClick = (schedule) => {
    setSelectedSchedule(schedule);
    setShowScheduleModal(true);
  };

  const closeScheduleModal = () => {
    setShowScheduleModal(false);
    setSelectedSchedule(null);
  };

  // Calendar navigation functions
  const navigateMonth = (direction) => {
    setCalendarDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    setCalendarDate(new Date());
    // Add slight delay to ensure calendar re-renders before scrolling
    setTimeout(() => {
      scrollToToday();
    }, 100);
  };

  // Function to scroll today's date to the center of the view
  const scrollToToday = useCallback(() => {
    if (currentView === "calendar" && calendarScrollRef.current) {
      const container = calendarScrollRef.current;

      // Function to attempt centering with retries
      const attemptCentering = (attempts = 0) => {
        const todayElement = container.querySelector(".calendar-day.today");

        if (todayElement) {
          const containerRect = container.getBoundingClientRect();
          const elementRect = todayElement.getBoundingClientRect();
          const containerHeight = container.clientHeight;
          const scrollTop = container.scrollTop;

          // Calculate the element's position relative to container's current scroll
          const elementTop = elementRect.top - containerRect.top + scrollTop;
          const elementHeight = elementRect.height;

          // Calculate scroll position to center today's date
          const targetScrollTop =
            elementTop - containerHeight / 2 + elementHeight / 2;

          container.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: "smooth",
          });
        } else if (attempts < 3) {
          // Retry up to 3 times if today element not found
          setTimeout(() => attemptCentering(attempts + 1), 100);
        }
      };

      // Small delay to ensure DOM is ready, then attempt centering
      setTimeout(() => attemptCentering(), 50);
    }
  }, [currentView]);

  // Center calendar on today when component loads or switches to calendar view
  useEffect(() => {
    if (currentView === "calendar" && filteredSchedules.length > 0) {
      // Add delay to ensure DOM is ready
      const timer = setTimeout(() => {
        scrollToToday();
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [currentView, filteredSchedules, scrollToToday]);

  // Initial centering when app first loads with calendar as default view
  useEffect(() => {
    if (currentView === "calendar") {
      const timer = setTimeout(() => {
        scrollToToday();
      }, 500); // Longer delay for initial load

      return () => clearTimeout(timer);
    }
  }, [scrollToToday, currentView]);

  const groupSchedulesByDate = (schedules) => {
    const grouped = {};
    schedules.forEach((schedule) => {
      const date = new Date(schedule.scheduledFor).toDateString();
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(schedule);
    });
    return grouped;
  };

  const renderCalendarView = () => {
    const schedulesByDate = groupSchedulesByDate(filteredSchedules);

    // Helper function to check if a date is within the 10-day period starting from today
    const isWithinTenDayPeriod = (date) => {
      const startDate = new Date(today);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() + 9); // 10 days including today
      endDate.setHours(23, 59, 59, 999);

      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);

      return checkDate >= startDate && checkDate <= endDate;
    };

    // Count schedules in the 10-day period
    const tenDayPeriodSchedules = filteredSchedules.filter((schedule) => {
      const scheduleDate = new Date(schedule.scheduledFor);
      return isWithinTenDayPeriod(scheduleDate);
    });

    // Generate calendar days properly aligned to weeks
    const startDate = new Date(calendarDate);
    startDate.setMonth(startDate.getMonth() - 1);
    startDate.setDate(1); // Start from 1st of previous month

    // Find the first Sunday of the calendar view (may be in previous month)
    const firstSunday = new Date(startDate);
    while (firstSunday.getDay() !== 0) {
      firstSunday.setDate(firstSunday.getDate() - 1);
    }

    const endDate = new Date(calendarDate);
    endDate.setMonth(endDate.getMonth() + 2);
    endDate.setDate(0); // End at last day of next month

    // Find the last Saturday of the calendar view (may be in next month)
    const lastSaturday = new Date(endDate);
    while (lastSaturday.getDay() !== 6) {
      lastSaturday.setDate(lastSaturday.getDate() + 1);
    }

    // Generate all days from first Sunday to last Saturday
    const allDays = [];
    const current = new Date(firstSunday);
    while (current <= lastSaturday) {
      allDays.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    // Group days into weeks (each week will have exactly 7 days)
    const weeks = [];
    for (let i = 0; i < allDays.length; i += 7) {
      const week = allDays.slice(i, i + 7);
      weeks.push(week);
    }

    return (
      <div className="calendar-view">
        {/* Enhanced calendar header with better navigation */}
        <div className="calendar-header">
          <div className="calendar-nav">
            <button
              className="nav-btn nav-btn-prev"
              onClick={() => navigateMonth(-1)}
              title="Previous Month"
            >
              ‹
            </button>
            <div className="calendar-title">
              <h3>
                {calendarDate.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </h3>
              <div className="month-summary">
                {
                  filteredSchedules.filter((schedule) => {
                    const scheduleDate = new Date(schedule.scheduledFor);
                    return (
                      scheduleDate.getMonth() === calendarDate.getMonth() &&
                      scheduleDate.getFullYear() === calendarDate.getFullYear()
                    );
                  }).length
                }{" "}
                schedules this month
              </div>
              <div className="ten-day-summary">
                <span className="ten-day-count">
                  {tenDayPeriodSchedules.length}
                </span>{" "}
                schedules in next 10 days
              </div>
            </div>
            <button
              className="nav-btn nav-btn-next"
              onClick={() => navigateMonth(1)}
              title="Next Month"
            >
              ›
            </button>
          </div>
          <div className="calendar-controls">
            <button
              className="today-btn"
              onClick={goToToday}
              title="Go to Today"
            >
              Today
            </button>
            <button
              className="view-toggle-btn"
              onClick={() => setCurrentView("list")}
              title="Switch to List View"
            >
              List View
            </button>
          </div>
        </div>

        {/* Continuous calendar with month labels */}
        <div ref={calendarScrollRef} className="calendar-scroll-container">
          <div className="calendar-continuous">
            {/* Day headers */}
            <div className="calendar-week-header">
              <div className="month-label-column"></div>
              {[
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
              ].map((dayName, index) => (
                <div key={index} className="calendar-day-header">
                  <span className="day-full">{dayName}</span>
                  <span className="day-short">{dayName.substring(0, 3)}</span>
                </div>
              ))}
            </div>

            {/* Calendar weeks */}
            {weeks.map((week, weekIndex) => {
              const firstDayOfWeek = week[0];

              // Show month label only for the first week of each month
              // or if this is the first week in the calendar
              let monthLabel = "";
              if (weekIndex === 0 || week.some((day) => day.getDate() === 1)) {
                // Find the first day that's the 1st of a month, or use first day of week
                const monthDay =
                  week.find((day) => day.getDate() === 1) || firstDayOfWeek;
                monthLabel = monthDay.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                });
              }

              return (
                <div key={weekIndex} className="calendar-week">
                  <div className="month-label-column">
                    {monthLabel && (
                      <div className="month-label">{monthLabel}</div>
                    )}
                  </div>

                  {week.map((day, dayIndex) => {
                    const dayString = day.toDateString();
                    const daySchedules = schedulesByDate[dayString] || [];
                    const isToday = day.toDateString() === today.toDateString();
                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                    const isInTenDayPeriod = isWithinTenDayPeriod(day);
                    const currentViewMonth = calendarDate.getMonth();
                    const currentViewYear = calendarDate.getFullYear();
                    const isCurrentViewMonth =
                      day.getMonth() === currentViewMonth &&
                      day.getFullYear() === currentViewYear;

                    return (
                      <div
                        key={dayIndex}
                        className={`calendar-day ${
                          !isCurrentViewMonth ? "other-month" : ""
                        } ${isToday ? "today" : ""} ${
                          isWeekend ? "weekend" : ""
                        } ${daySchedules.length > 0 ? "has-schedules" : ""} ${
                          isInTenDayPeriod ? "ten-day-period" : ""
                        }`}
                        onClick={() =>
                          daySchedules.length > 0 &&
                          handleScheduleClick(daySchedules[0])
                        }
                      >
                        <div className="day-number">
                          {day.getDate()}
                          {isInTenDayPeriod && (
                            <span
                              className="ten-day-indicator"
                              title="Next 10 days period"
                            >
                              •
                            </span>
                          )}
                        </div>
                        <div className="day-schedules">
                          {daySchedules.slice(0, 3).map((schedule) => (
                            <div
                              key={schedule.scheduleId || schedule.id}
                              className={`schedule-item ${schedule.status.toLowerCase()}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleScheduleClick(schedule);
                              }}
                              title={`Schedule #${
                                schedule.scheduleId || schedule.id
                              } - ${schedule.status} - ${new Date(
                                schedule.scheduledFor,
                              ).toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}`}
                            >
                              <span className="schedule-id">
                                #{schedule.scheduleId || schedule.id}
                              </span>
                              <span className="schedule-time">
                                {new Date(
                                  schedule.scheduledFor,
                                ).toLocaleTimeString("en-US", {
                                  hour: "numeric",
                                  minute: "2-digit",
                                })}
                              </span>
                            </div>
                          ))}
                          {daySchedules.length > 3 && (
                            <div
                              className="schedule-overflow"
                              onClick={() =>
                                handleScheduleClick(daySchedules[0])
                              }
                            >
                              +{daySchedules.length - 3} more
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };
  const renderScheduleModal = () => {
    if (!selectedSchedule || !showScheduleModal) return null;

    const scheduleId = selectedSchedule.scheduleId || selectedSchedule.id;
    const messageUuid = selectedSchedule.messageUuid;
    const messageData = messageDetails[messageUuid];

    const messageSubject =
      messageData?.subject ||
      selectedSchedule.messageSubject ||
      selectedSchedule.subject ||
      "No subject";
    const messageContent =
      messageData?.content ||
      selectedSchedule.messageContent ||
      selectedSchedule.content ||
      "No content available";

    return (
      <div className="schedule-modal-overlay" onClick={closeScheduleModal}>
        <div className="schedule-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Schedule Details #{scheduleId}</h3>
            <button className="modal-close" onClick={closeScheduleModal}>
              ×
            </button>
          </div>

          <div className="modal-content">
            <div className="modal-section">
              <h4>Schedule Information</h4>
              <div className="detail-grid-compact">
                <div className="detail-row">
                  <div className="detail-item">
                    <span className="label">Status:</span>
                    <span
                      className={`status-badge ${selectedSchedule.status.toLowerCase()}`}
                    >
                      {selectedSchedule.status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Scheduled:</span>
                    <span className="value">
                      {formatDateTime(selectedSchedule.scheduledFor)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Channel:</span>
                    <span className="value">
                      {selectedSchedule.communicationChannel || "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Msg ID:</span>
                    <span className="value">
                      {selectedSchedule.messageId || "N/A"}
                    </span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-item">
                    <span className="label">Sophistication:</span>
                    <span className="value">
                      {selectedSchedule.sophisticationLevel || "N/A"}/10
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Context:</span>
                    <span className="value">
                      {selectedSchedule.communicationContext || "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Frequency:</span>
                    <span className="value">
                      {selectedSchedule.frequency || "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Profile:</span>
                    <span className="value">
                      {selectedSchedule.profileName ||
                        selectedSchedule.simulationProfileId ||
                        "N/A"}
                    </span>
                  </div>
                </div>
                {selectedSchedule.executedAt && (
                  <div className="detail-row">
                    <div className="detail-item">
                      <span className="label">Executed:</span>
                      <span className="value">
                        {formatDateTime(selectedSchedule.executedAt)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-section">
              <h4>Message</h4>
              <div className="message-details">
                <div className="detail-item">
                  <span className="label">Subject:</span>
                  <span className="value">{messageSubject}</span>
                </div>
                <div
                  className="detail-item"
                  style={{ flexDirection: "column", alignItems: "stretch" }}
                >
                  <span className="label">Content:</span>
                  <div className="message-content">{messageContent}</div>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              {selectedSchedule.status === "PENDING" && (
                <button
                  className="btn-execute"
                  onClick={() => {
                    handleExecuteSchedule(scheduleId);
                    closeScheduleModal();
                  }}
                  disabled={executingSchedules.has(scheduleId)}
                >
                  {executingSchedules.has(scheduleId)
                    ? "Executing..."
                    : "Execute Now"}
                </button>
              )}

              {selectedSchedule.status !== "EXECUTING" && (
                <button
                  className="btn-delete"
                  onClick={() => {
                    handleDeleteSchedule(scheduleId);
                    closeScheduleModal();
                  }}
                  disabled={deletingSchedules.has(scheduleId)}
                >
                  {deletingSchedules.has(scheduleId) ? "Deleting..." : "Delete"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="execute-simulation-schedule">
      <div className="header-section">
        <div className="header-main">
          <div className="header-title-area">
            <h2>Execute Simulation Schedules</h2>
            {selectedUser && (
              <div className="user-info">
                <span className="user-label">Selected User:</span>
                <span className="user-details">
                  {selectedUser.firstName} {selectedUser.lastName} (
                  {selectedUser.email})
                </span>
              </div>
            )}
          </div>

          {/* Top Right Controls */}
          <div className="top-right-controls">
            <div className="schedule-info">
              <span className="schedule-count">
                {allSchedules.length} total schedule
                {allSchedules.length !== 1 ? "s" : ""}
              </span>
              {hasActiveSearch() && (
                <span className="filtered-count">
                  {filteredSchedules.length} matching filter
                  {filteredSchedules.length !== 1 ? "s" : ""}
                </span>
              )}
              {selectedUser && (
                <span className="user-schedule-count">
                  {schedules.length} for selected user
                </span>
              )}
            </div>

            <div className="action-buttons">
              <div className="primary-actions">
                <div className="view-toggle">
                  <button
                    className={`view-btn ${
                      currentView === "calendar" ? "active" : ""
                    }`}
                    onClick={() => setCurrentView("calendar")}
                    title="Calendar View"
                  >
                    📅 Calendar
                  </button>
                  <button
                    className={`view-btn ${
                      currentView === "list" ? "active" : ""
                    }`}
                    onClick={() => setCurrentView("list")}
                    title="List View"
                  >
                    📋 List
                  </button>
                </div>

                <button
                  className="btn-search-toggle"
                  onClick={() => setShowSearchPanel(!showSearchPanel)}
                  title="Toggle search panel"
                >
                  � {showSearchPanel ? "Hide" : "Search"}
                </button>
              </div>

              <div className="secondary-actions">
                <RefreshButton onClick={fetchSchedules} loading={loading} />

                <button
                  className="btn-execute-all"
                  onClick={handleExecuteAllSchedules}
                  disabled={
                    executingAll || loading || allSchedules.length === 0
                  }
                  title="Execute all pending schedules"
                >
                  {executingAll ? "⚡ Executing..." : "⚡ Execute All"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Panel */}
      {showSearchPanel && (
        <div className="search-panel">
          <div className="search-panel-header">
            <h3>Search & Filter Schedules</h3>
            {hasActiveSearch() && (
              <button className="btn-clear-search" onClick={clearSearch}>
                Clear All Filters
              </button>
            )}
          </div>

          <div className="search-fields">
            <div className="search-row">
              <div className="search-field search-field-range">
                <DateRangeField
                  legend="Scheduled between"
                  fromLabel="Date from"
                  toLabel="Date to"
                  futureAllowed
                  from={searchCriteria.dateFrom}
                  to={searchCriteria.dateTo}
                  onChange={({ from, to }) =>
                    setSearchCriteria((prev) => ({
                      ...prev,
                      dateFrom: from,
                      dateTo: to,
                    }))
                  }
                />
              </div>

              <div className="search-field">
                <label>User Email:</label>
                <select
                  value={searchCriteria.userEmail}
                  onChange={(e) =>
                    handleSearchChange("userEmail", e.target.value)
                  }
                  className="search-select"
                >
                  <option value="">All Users</option>
                  {uniqueUserEmails.map((email) => (
                    <option key={email} value={email}>
                      {email}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="search-row">
              <div className="search-field">
                <label>Status:</label>
                <select
                  value={searchCriteria.status}
                  onChange={(e) => handleSearchChange("status", e.target.value)}
                  className="search-select"
                >
                  <option value="">All Statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="EXECUTING">Executing</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="FAILED">Failed</option>
                </select>
              </div>

              <div className="search-field">
                <label>Channel:</label>
                <select
                  value={searchCriteria.channel}
                  onChange={(e) =>
                    handleSearchChange("channel", e.target.value)
                  }
                  className="search-select"
                >
                  <option value="">All Channels</option>
                  {uniqueChannels.map((channel) => (
                    <option key={channel} value={channel}>
                      {channel}
                    </option>
                  ))}
                </select>
              </div>

              <div className="search-field search-results">
                <div className="search-summary">
                  <strong>Results: {filteredSchedules.length}</strong> schedule
                  {filteredSchedules.length !== 1 ? "s" : ""} found
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-indicator">
          <LoadingSpinner
            size="sm"
            message="Loading schedules..."
            className="compact"
          />
        </div>
      )}

      {message && (
        <div
          className={`message ${
            message.includes("Error") ? "error" : "success"
          }`}
        >
          {message}
        </div>
      )}

      <div className="schedules-container">
        {!loading && filteredSchedules.length === 0 ? (
          <div className="no-schedules">
            {hasActiveSearch() ? (
              <>
                <p>No schedules match your search criteria.</p>
                <button className="btn-clear-search" onClick={clearSearch}>
                  Clear Search Filters
                </button>
              </>
            ) : (
              <>
                <p>No simulation schedules found.</p>
                <p>
                  Create a schedule first using the "Create Simulation Schedule"
                  option.
                </p>
              </>
            )}
          </div>
        ) : (
          !loading &&
          (currentView === "calendar"
            ? renderCalendarView()
            : renderSchedulesByUser())
        )}
      </div>

      {/* Schedule Details Modal */}
      {renderScheduleModal()}

      {!selectedUser && !loading && (
        <div className="info-message">
          <p>
            Showing all users' simulation schedules. Select a user from the
            Member User Info section to view only their schedules.
          </p>
        </div>
      )}
    </div>
  );
}

export default ExecuteSimulationSchedule;
